import { CryptoCore } from '../../../secure-json/core/crypto-core';
import { CollectionName } from '../../constants';
import {
  CurrentMigrationCollection,
  MigratedMigrationCollection,
  MigratedMigrationCollectionsData,
  MigrationType,
} from '../types';

type CurrentInboxAttachment = { encryptedSymmetricKey: string; filePath: string };
type CurrentInboxDto = {
  attachments: CurrentInboxAttachment[];
};

type MigratedInboxAttachment = { symmetricKey: string; filePath: string };
type MigratedInboxDto = {
  attachments: MigratedInboxAttachment[];
};

const migrateInboxCollection = async ({
  inboxCollection,
  privateKey,
}: {
  inboxCollection: CurrentMigrationCollection | undefined;
  privateKey: string;
}) => {
  const migratedCollection: MigratedMigrationCollection<MigratedInboxDto> = new Map();

  if (!inboxCollection) {
    return undefined;
  }

  const cryptoCore = new CryptoCore();

  for (const [key, collectionItem] of inboxCollection.entries()) {
    const { attachments, ...restCollectionItem } = collectionItem as CurrentInboxDto;
    const newAttachments: MigratedInboxAttachment[] = [];

    for (const attachment of attachments) {
      const { encryptedSymmetricKey, ...restAttachment } = attachment;

      const symmetricKey = await cryptoCore.asymmetric.decrypt(encryptedSymmetricKey, privateKey);
      newAttachments.push({ ...restAttachment, symmetricKey });
    }

    migratedCollection.set(key, { ...restCollectionItem, attachments: newAttachments });
  }

  return migratedCollection;
};

type CurrentEncryptedFileKeyDto = {
  encryptedSymmetricKey: string;
};
type MigratedEncryptedFileKeyDto = {
  symmetricKey: string;
};

const migrateEncryptedFileKeyCollection = async ({
  encryptedFileKeyCollection,
  privateKey,
}: {
  encryptedFileKeyCollection: CurrentMigrationCollection | undefined;
  privateKey: string;
}) => {
  const migratedCollection: MigratedMigrationCollection<MigratedEncryptedFileKeyDto> = new Map();

  if (!encryptedFileKeyCollection) {
    return undefined;
  }

  const cryptoCore = new CryptoCore();

  for (const [key, collectionItem] of encryptedFileKeyCollection.entries()) {
    const { encryptedSymmetricKey, ...restCollectionItem } =
      collectionItem as CurrentEncryptedFileKeyDto;

    const symmetricKey = await cryptoCore.asymmetric.decrypt(encryptedSymmetricKey, privateKey);

    migratedCollection.set(key, { ...restCollectionItem, symmetricKey });
  }

  return migratedCollection;
};

const migrate: MigrationType['migrate'] = async (data, props) => {
  const migratedData: MigratedMigrationCollectionsData = new Map();

  const encryptedFileKeyCollection = data.get(CollectionName.ENCRYPTED_FILE_KEY);
  const inboxCollection = data.get(CollectionName.INBOX);

  const encryptedFileKeyCollectionPromise = migrateEncryptedFileKeyCollection({
    encryptedFileKeyCollection,
    privateKey: props.keyPair.privateKey,
  });

  const inboxCollectionPromise = migrateInboxCollection({
    inboxCollection,
    privateKey: props.keyPair.privateKey,
  });

  const [migratedEncryptedFileKeyCollection, migratedInboxCollection] = await Promise.all([
    encryptedFileKeyCollectionPromise,
    inboxCollectionPromise,
  ]);

  if (migratedEncryptedFileKeyCollection) {
    migratedData.set(CollectionName.ENCRYPTED_FILE_KEY, migratedEncryptedFileKeyCollection);
  }

  if (migratedInboxCollection) {
    migratedData.set(CollectionName.INBOX, migratedInboxCollection);
  }

  return migratedData;
};

export const migration2: MigrationType = {
  migrate,
  version: 3,
};
